import React, { Fragment, useReducer, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import ai from "../../helpers/axios";
import YomTierRepeater from "./yom-tier-repeater";
import WifiTierRepeater from "./wifi-tier-repeater";
import SwitchInput from "../reusable-modules/switch-input";
import "./click-to-fly-pricing-config.css";

// Utility functions for percentage conversion
const toPercentageDisplay = (decimal) => {
  if (decimal === null || decimal === undefined || decimal === "") return "";
  return (decimal * 100).toFixed(2);
};

const toDecimal = (percentageInput) => {
  if (
    percentageInput === "" ||
    percentageInput === null ||
    percentageInput === undefined
  )
    return 0;
  return parseFloat(percentageInput) / 100;
};

const formatHour = (hour) => {
  return `${hour}:00`;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }
    case "dataDownloaded": {
      return {
        ...state,
        config: action.payload.config,
        originalConfig: JSON.parse(JSON.stringify(action.payload.config)), // deep copy
        peakDayCategories: action.payload.peakDayCategories || [],
        loading: false,
        isDirty: false,
      };
    }
    case "yomTierAdded": {
      const updatedYomFactors = state.config.yomAdjustmentFactors.map(
        (factor) => {
          if (
            factor.aircraftTypeId === action.aircraftTypeId ||
            factor.aircraftTypeId._id === action.aircraftTypeId
          ) {
            return {
              ...factor,
              yomTiers: [
                ...factor.yomTiers,
                { year: null, commonlyAvailable: "", adjustment: 0 },
              ],
            };
          }
          return factor;
        }
      );
      return {
        ...state,
        config: {
          ...state.config,
          yomAdjustmentFactors: updatedYomFactors,
        },
        isDirty: true,
      };
    }
    case "yomTierRemoved": {
      const updatedYomFactors = state.config.yomAdjustmentFactors.map(
        (factor) => {
          if (
            factor.aircraftTypeId === action.aircraftTypeId ||
            factor.aircraftTypeId._id === action.aircraftTypeId
          ) {
            const newTiers = [...factor.yomTiers];
            newTiers.splice(action.tierIndex, 1);
            return {
              ...factor,
              yomTiers: newTiers,
            };
          }
          return factor;
        }
      );
      return {
        ...state,
        config: {
          ...state.config,
          yomAdjustmentFactors: updatedYomFactors,
        },
        isDirty: true,
      };
    }
    case "yomTierFieldChanged": {
      const updatedYomFactors = state.config.yomAdjustmentFactors.map(
        (factor) => {
          if (
            factor.aircraftTypeId === action.aircraftTypeId ||
            factor.aircraftTypeId._id === action.aircraftTypeId
          ) {
            const newTiers = [...factor.yomTiers];
            newTiers[action.tierIndex] = {
              ...newTiers[action.tierIndex],
              [action.field]: action.value,
            };
            return {
              ...factor,
              yomTiers: newTiers,
            };
          }
          return factor;
        }
      );
      return {
        ...state,
        config: {
          ...state.config,
          yomAdjustmentFactors: updatedYomFactors,
        },
        isDirty: true,
      };
    }
    case "wifiTierAdded": {
      const updatedWifiFactors = (state.config.wifiAddOnFactors || []).map(
        (factor) => {
          if (
            factor.aircraftTypeId === action.aircraftTypeId ||
            factor.aircraftTypeId._id === action.aircraftTypeId
          ) {
            return {
              ...factor,
              wifiTiers: [
                ...factor.wifiTiers,
                { year: null, addOnPercent: null },
              ],
            };
          }
          return factor;
        }
      );
      return {
        ...state,
        config: {
          ...state.config,
          wifiAddOnFactors: updatedWifiFactors,
        },
        isDirty: true,
      };
    }
    case "wifiTierRemoved": {
      const updatedWifiFactors = (state.config.wifiAddOnFactors || []).map(
        (factor) => {
          if (
            factor.aircraftTypeId === action.aircraftTypeId ||
            factor.aircraftTypeId._id === action.aircraftTypeId
          ) {
            const newTiers = [...factor.wifiTiers];
            newTiers.splice(action.tierIndex, 1);
            return {
              ...factor,
              wifiTiers: newTiers,
            };
          }
          return factor;
        }
      );
      return {
        ...state,
        config: {
          ...state.config,
          wifiAddOnFactors: updatedWifiFactors,
        },
        isDirty: true,
      };
    }
    case "wifiTierFieldChanged": {
      const updatedWifiFactors = (state.config.wifiAddOnFactors || []).map(
        (factor) => {
          if (
            factor.aircraftTypeId === action.aircraftTypeId ||
            factor.aircraftTypeId._id === action.aircraftTypeId
          ) {
            const newTiers = [...factor.wifiTiers];
            newTiers[action.tierIndex] = {
              ...newTiers[action.tierIndex],
              [action.field]: action.value,
            };
            return {
              ...factor,
              wifiTiers: newTiers,
            };
          }
          return factor;
        }
      );
      return {
        ...state,
        config: {
          ...state.config,
          wifiAddOnFactors: updatedWifiFactors,
        },
        isDirty: true,
      };
    }
    case "timeRangeAdded": {
      return {
        ...state,
        config: {
          ...state.config,
          timeAdjustmentFactors: [
            ...state.config.timeAdjustmentFactors,
            { label: "", startTime: 0, endTime: 23, adjustment: 0 },
          ],
        },
        isDirty: true,
      };
    }
    case "timeRangeRemoved": {
      const newTimeFactors = [...state.config.timeAdjustmentFactors];
      newTimeFactors.splice(action.index, 1);
      return {
        ...state,
        config: {
          ...state.config,
          timeAdjustmentFactors: newTimeFactors,
        },
        isDirty: true,
      };
    }
    case "timeFactorFieldChanged": {
      const newTimeFactors = [...state.config.timeAdjustmentFactors];
      newTimeFactors[action.index] = {
        ...newTimeFactors[action.index],
        [action.field]: action.value,
      };
      return {
        ...state,
        config: {
          ...state.config,
          timeAdjustmentFactors: newTimeFactors,
        },
        isDirty: true,
      };
    }
    case "peakNoticeFieldChanged": {
      const newPeakNotice = [...state.config.peakDayNoticeRequirements];
      newPeakNotice[action.index] = {
        ...newPeakNotice[action.index],
        [action.field]: action.value,
      };
      return {
        ...state,
        config: {
          ...state.config,
          peakDayNoticeRequirements: newPeakNotice,
        },
        isDirty: true,
      };
    }
    case "enableClickToFlyToggled": {
      return {
        ...state,
        config: {
          ...state.config,
          enableClickToFly: action.value,
        },
        isDirty: true,
      };
    }
    case "memberDiscountRateChanged": {
      return {
        ...state,
        config: {
          ...state.config,
          memberDiscountRate: action.value,
        },
        isDirty: true,
      };
    }
    case "nonMemberDiscountRateChanged": {
      return {
        ...state,
        config: {
          ...state.config,
          nonMemberDiscountRate: action.value,
        },
        isDirty: true,
      };
    }
    case "tdrFlexDiscountChanged": {
      const newTdrDiscounts = [...state.config.departureTdrFlexDiscounts];
      newTdrDiscounts[action.index] = {
        ...newTdrDiscounts[action.index],
        [action.field]: action.value,
      };
      return {
        ...state,
        config: {
          ...state.config,
          departureTdrFlexDiscounts: newTdrDiscounts,
        },
        isDirty: true,
      };
    }
    case "priceRangeAdded": {
      return {
        ...state,
        config: {
          ...state.config,
          priceRangeAdjustments: [
            ...state.config.priceRangeAdjustments,
            {
              minPrice: 0,
              maxPrice: 0,
              owPpt2DrtAdjustment: 1.0,
              otgAdjustment: 1.0,
            },
          ],
        },
        isDirty: true,
      };
    }
    case "priceRangeRemoved": {
      const newPriceRanges = [...state.config.priceRangeAdjustments];
      newPriceRanges.splice(action.index, 1);
      return {
        ...state,
        config: {
          ...state.config,
          priceRangeAdjustments: newPriceRanges,
        },
        isDirty: true,
      };
    }
    case "priceRangeFieldChanged": {
      const newPriceRanges = [...state.config.priceRangeAdjustments];
      newPriceRanges[action.index] = {
        ...newPriceRanges[action.index],
        [action.field]: action.value,
      };
      return {
        ...state,
        config: {
          ...state.config,
          priceRangeAdjustments: newPriceRanges,
        },
        isDirty: true,
      };
    }
    case "exactTimeSelectionFieldChanged": {
      return {
        ...state,
        config: {
          ...state.config,
          exactTimeSelection: {
            ...state.config.exactTimeSelection,
            [action.field]: action.value,
          },
        },
        isDirty: true,
      };
    }
    case "configReset": {
      return {
        ...state,
        config: JSON.parse(JSON.stringify(state.originalConfig)), // deep copy
        isDirty: false,
      };
    }
    case "savingStarted": {
      return {
        ...state,
        saving: true,
      };
    }
    case "savingCompleted": {
      return {
        ...state,
        config: action.payload.config,
        originalConfig: JSON.parse(JSON.stringify(action.payload.config)), // deep copy
        saving: false,
        isDirty: false,
      };
    }
    case "savingFailed": {
      return {
        ...state,
        saving: false,
      };
    }
    default:
      break;
  }
  return state;
};

const ClickToFlyPricingConfigContent = () => {
  const initialState = {
    config: {
      enableClickToFly: true,
      memberDiscountRate: 0.05,
      nonMemberDiscountRate: 0,
      yomAdjustmentFactors: [],
      timeAdjustmentFactors: [],
      peakDayNoticeRequirements: [],
      priceRangeAdjustments: [],
      departureTdrFlexDiscounts: [],
      exactTimeSelection: null,
      wifiAddOnFactors: [],
    },
    originalConfig: null,
    peakDayCategories: [],
    loading: true,
    saving: false,
    isDirty: false,
  };

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [activeYomTab, setActiveYomTab] = useState(0);
  const [activeWifiTab, setActiveWifiTab] = useState(0);

  // Fetch pricing config data
  const getData = async () => {
    dispatch({ type: "dataBeganDownloading" });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      // Fetch pricing config (backend populates peakDayCategoryId with peakValue, label, name)
      const { data: config } = await ai
        .auth(token)
        .get(`/api/clicktofly/pricing-config`);

      // Extract peak day categories from populated peakDayCategoryId objects
      let peakDayCategories = [];
      if (
        config.peakDayNoticeRequirements &&
        config.peakDayNoticeRequirements.length > 0
      ) {
        peakDayCategories = config.peakDayNoticeRequirements
          .map((req) => req.peakDayCategoryId)
          .filter((cat) => cat && typeof cat === "object" && cat._id);
      }

      dispatch({
        type: "dataDownloaded",
        payload: {
          config,
          peakDayCategories,
        },
      });
    } catch (error) {
      console.error("Error fetching pricing config:", error);
      toast.error("Failed to load pricing configuration");
      dispatch({
        type: "dataDownloaded",
        payload: { config: initialState.config, peakDayCategories: [] },
      });
    }
  };

  useEffect(() => {
    getData();
    document.title = "ClickToFly Pricing Configuration | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // YOM Tier handlers
  const handleYomTierAdd = (aircraftTypeId) => {
    dispatch({ type: "yomTierAdded", aircraftTypeId });
  };

  const handleYomTierRemove = (aircraftTypeId, tierIndex) => {
    dispatch({ type: "yomTierRemoved", aircraftTypeId, tierIndex });
  };

  const handleYomTierChange = (aircraftTypeId, tierIndex, field, value) => {
    dispatch({
      type: "yomTierFieldChanged",
      aircraftTypeId,
      tierIndex,
      field,
      value,
    });
  };

  // WiFi Tier handlers
  const handleWifiTierAdd = (aircraftTypeId) => {
    dispatch({ type: "wifiTierAdded", aircraftTypeId });
  };

  const handleWifiTierRemove = (aircraftTypeId, tierIndex) => {
    dispatch({ type: "wifiTierRemoved", aircraftTypeId, tierIndex });
  };

  const handleWifiTierChange = (aircraftTypeId, tierIndex, field, value) => {
    dispatch({
      type: "wifiTierFieldChanged",
      aircraftTypeId,
      tierIndex,
      field,
      value,
    });
  };

  // Time range handlers
  const handleTimeRangeAdd = () => {
    dispatch({ type: "timeRangeAdded" });
  };

  const handleTimeRangeRemove = (index) => {
    dispatch({ type: "timeRangeRemoved", index });
  };

  const handleTimeFactorChange = (index, field, value) => {
    let processedValue = value;

    if (field === "startTime" || field === "endTime") {
      processedValue = value === "" ? 0 : parseInt(value);
    } else if (field === "adjustment") {
      processedValue = value === "" ? 0 : toDecimal(value);
    }

    dispatch({
      type: "timeFactorFieldChanged",
      index,
      field,
      value: processedValue,
    });
  };

  // Peak notice handlers
  const handlePeakNoticeChange = (index, field, value) => {
    const processedValue = value === "" ? 0 : parseInt(value);
    dispatch({ type: "peakNoticeFieldChanged", index, field, value: processedValue });
  };

  // Price range handlers
  const handlePriceRangeAdd = () => {
    dispatch({ type: "priceRangeAdded" });
  };

  const handlePriceRangeRemove = (index) => {
    dispatch({ type: "priceRangeRemoved", index });
  };

  const handlePriceRangeChange = (index, field, value) => {
    let processedValue = value;

    if (field === "minPrice" || field === "maxPrice") {
      processedValue = value === "" ? 0 : parseInt(value);
    } else if (field === "owPpt2DrtAdjustment" || field === "otgAdjustment") {
      // Convert percentage to decimal (e.g., 112.5 becomes 1.125)
      processedValue = value === "" ? 1.0 : parseFloat(value) / 100;
    }

    dispatch({
      type: "priceRangeFieldChanged",
      index,
      field,
      value: processedValue,
    });
  };

  // Format price for display
  const formatPrice = (price) => {
    if (price >= 1000000) return "∞";
    return "$" + price.toLocaleString();
  };

  // Enable ClickToFly toggle handler with auto-save
  const handleEnableClickToFlyToggle = async (e) => {
    const newValue = e.target.checked;

    // Auto-save only this field
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      // Build payload with the new enableClickToFly value
      const payload = buildPayload();
      payload.enableClickToFly = newValue;

      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      // Only update state after successful save with server data
      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success(`ClickToFly ${newValue ? "enabled" : "disabled"}`, {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error updating ClickToFly enable status:", error);
      toast.error("Failed to update ClickToFly status");
    }
  };

  // Build payload helper
  const buildPayload = () => {
    return {
      enableClickToFly: state.config.enableClickToFly,
      memberDiscountRate: state.config.memberDiscountRate,
      nonMemberDiscountRate: state.config.nonMemberDiscountRate,
      yomAdjustmentFactors: state.config.yomAdjustmentFactors.map((factor) => ({
        aircraftTypeId:
          typeof factor.aircraftTypeId === "object"
            ? factor.aircraftTypeId._id || factor.aircraftTypeId.$oid
            : factor.aircraftTypeId,
        aircraftTypeName: factor.aircraftTypeName,
        yomTiers: factor.yomTiers,
      })),
      timeAdjustmentFactors: state.config.timeAdjustmentFactors,
      peakDayNoticeRequirements: state.config.peakDayNoticeRequirements.map(
        (req) => ({
          peakDayCategoryId:
            typeof req.peakDayCategoryId === "object"
              ? req.peakDayCategoryId._id || req.peakDayCategoryId.$oid
              : req.peakDayCategoryId,
          owPpt2Days: req.owPpt2Days,
          otgDrtDays: req.otgDrtDays,
        })
      ),
      priceRangeAdjustments: state.config.priceRangeAdjustments || [],
      departureTdrFlexDiscounts: state.config.departureTdrFlexDiscounts || [],
      exactTimeSelection: state.config.exactTimeSelection,
      wifiAddOnFactors: (state.config.wifiAddOnFactors || []).map((factor) => ({
        aircraftTypeId:
          typeof factor.aircraftTypeId === "object"
            ? factor.aircraftTypeId._id || factor.aircraftTypeId.$oid
            : factor.aircraftTypeId,
        aircraftTypeName: factor.aircraftTypeName,
        wifiTiers: factor.wifiTiers,
      })),
    };
  };

  // Exact time selection handler
  const handleExactTimeSelectionChange = (field, value) => {
    let processedValue = value;

    if (field === "startTime" || field === "endTime") {
      processedValue = value === "" ? 0 : parseInt(value);
    } else if (field === "stepInterval") {
      processedValue = value === "" ? 1 : parseInt(value);
    } else if (field === "adjustment") {
      processedValue = value === "" ? 0 : parseFloat(value) / 100;
    }

    dispatch({
      type: "exactTimeSelectionFieldChanged",
      field,
      value: processedValue,
    });
  };

  // Save exact time selection
  const handleSaveExactTimeSelection = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const payload = buildPayload();
      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success("Exact time selection settings saved!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving exact time selection:", error);
      toast.error("Failed to save exact time selection settings");
    }
  };

  // Member discount handlers
  const handleMemberDiscountRateChange = (e) => {
    const value = e.target.value === "" ? 0 : parseFloat(e.target.value) / 100;
    dispatch({ type: "memberDiscountRateChanged", value });
  };

  const handleNonMemberDiscountRateChange = (e) => {
    const value = e.target.value === "" ? 0 : parseFloat(e.target.value) / 100;
    dispatch({ type: "nonMemberDiscountRateChanged", value });
  };

  // TDR flex discount handlers
  const handleTdrFlexDiscountChange = (index, field, value) => {
    let processedValue = value;

    if (field === "tdrDays") {
      processedValue = value === "" ? 1 : parseInt(value);
    } else if (field === "discount") {
      // Convert percentage to decimal (e.g., 3 becomes 0.03)
      processedValue = value === "" ? 0 : parseFloat(value) / 100;
    }

    dispatch({
      type: "tdrFlexDiscountChanged",
      index,
      field,
      value: processedValue,
    });
  };

  // Save individual aircraft type YOM config
  const handleSaveAircraftTypeYom = async (aircraftTypeId) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const aircraftTypeFactor = state.config.yomAdjustmentFactors.find(
        (factor) =>
          (factor.aircraftTypeId._id ||
            factor.aircraftTypeId.$oid ||
            factor.aircraftTypeId) === aircraftTypeId
      );

      if (!aircraftTypeFactor) {
        toast.error("Aircraft type not found");
        return;
      }

      const payload = buildPayload();
      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success(
        `${aircraftTypeFactor.aircraftTypeName} YOM config saved!`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } catch (error) {
      console.error("Error saving aircraft type YOM config:", error);
      toast.error("Failed to save YOM configuration");
    }
  };

  // Save individual aircraft type WiFi config
  const handleSaveAircraftTypeWifi = async (aircraftTypeId) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const wifiFactor = (state.config.wifiAddOnFactors || []).find(
        (factor) =>
          (factor.aircraftTypeId._id ||
            factor.aircraftTypeId.$oid ||
            factor.aircraftTypeId) === aircraftTypeId
      );

      if (!wifiFactor) {
        toast.error("Aircraft type not found");
        return;
      }

      const payload = buildPayload();
      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success(
        `${wifiFactor.aircraftTypeName} WiFi config saved!`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } catch (error) {
      console.error("Error saving aircraft type WiFi config:", error);
      toast.error("Failed to save WiFi configuration");
    }
  };

  // Save time adjustments section
  const handleSaveTimeAdjustments = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const payload = buildPayload();
      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success("Time adjustments saved!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving time adjustments:", error);
      toast.error("Failed to save time adjustments");
    }
  };

  // Save peak notice section
  const handleSavePeakNotice = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const payload = buildPayload();
      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success("Peak notice requirements saved!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving peak notice:", error);
      toast.error("Failed to save peak notice requirements");
    }
  };

  // Save price ranges section
  const handleSavePriceRanges = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const payload = buildPayload();
      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success("Price range adjustments saved!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving price ranges:", error);
      toast.error("Failed to save price range adjustments");
    }
  };

  // Save member discount section
  const handleSaveMemberDiscount = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const payload = buildPayload();
      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success("Member discount setting saved!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving member discount:", error);
      toast.error("Failed to save member discount");
    }
  };

  // Save TDR flex discounts section
  const handleSaveTdrFlexDiscounts = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const payload = buildPayload();
      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success("TDR flex discounts saved!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving TDR flex discounts:", error);
      toast.error("Failed to save TDR flex discounts");
    }
  };

  // Get peak category label with peak value
  const getPeakCategoryLabel = (categoryId) => {
    const categoryIdStr =
      typeof categoryId === "object"
        ? categoryId._id || categoryId.$oid
        : categoryId;
    const category = state.peakDayCategories.find(
      (cat) => cat._id === categoryIdStr || cat._id === categoryId
    );
    if (category) {
      return `Peak ${category.peakValue} - ${
        category.label || category.name || "Category"
      }`;
    }
    return `Category ${categoryIdStr}`;
  };

  // Filter eligible aircraft types for ClickToFly (only show those used in routes)
  const eligibleAircraftTypes = [
    "Executive Turboprop",
    "Light Jet",
    "Midsize Jet",
    "Super Midsize Jet",
    "Heavy Jet - 10 Seats",
    "Heavy Jet - 12 Seats",
    "Heavy Jet - 14 Seats",
  ];

  const filteredYomFactors = state.config.yomAdjustmentFactors.filter(
    (factor) => eligibleAircraftTypes.includes(factor.aircraftTypeName)
  );

  // Form validation
  const validateForm = () => {
    const errors = [];

    // Validate time ranges
    state.config.timeAdjustmentFactors.forEach((factor, index) => {
      if (factor.startTime < 0 || factor.startTime > 23) {
        errors.push(`Time range ${index + 1}: Start time must be between 0-23`);
      }
      if (factor.endTime < 0 || factor.endTime > 23) {
        errors.push(`Time range ${index + 1}: End time must be between 0-23`);
      }
      if (factor.startTime >= factor.endTime) {
        errors.push(
          `Time range ${index + 1}: Start time must be less than end time`
        );
      }
    });

    // Validate peak notice days
    state.config.peakDayNoticeRequirements.forEach((req, index) => {
      if (req.owPpt2Days < 1) {
        errors.push(`Peak category ${index}: OW/PPT2 Days must be at least 1`);
      }
      if (req.otgDrtDays < 1) {
        errors.push(`Peak category ${index}: OTG/DRT Days must be at least 1`);
      }
    });

    // Validate YOM years
    state.config.yomAdjustmentFactors.forEach((factor) => {
      factor.yomTiers.forEach((tier, tierIndex) => {
        if (tier.year !== null && (tier.year < 1980 || tier.year > 2030)) {
          errors.push(
            `${factor.aircraftTypeName} tier ${
              tierIndex + 1
            }: Year must be between 1980-2030`
          );
        }
      });
    });

    return errors;
  };

  // Handle form submit
  const handleSubmit = async () => {
    // Validate form
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    dispatch({ type: "savingStarted" });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const payload = buildPayload();

      const { data } = await ai
        .auth(token)
        .put(`/api/clicktofly/pricing-config`, payload);

      dispatch({
        type: "savingCompleted",
        payload: { config: data },
      });

      toast.success("Pricing configuration updated successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error("Error saving pricing config:", error);
      toast.error("Failed to save pricing configuration");
      dispatch({ type: "savingFailed" });
    }
  };

  // Handle reset
  const handleReset = () => {
    if (window.confirm("Are you sure you want to discard all changes?")) {
      dispatch({ type: "configReset" });
      toast.info("Changes discarded");
    }
  };

  if (state.loading) {
    return (
      <Fragment>
        <h2>ClickToFly Pricing Configuration</h2>
        <p>Loading configuration...</p>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <h2 className="ctf-config-title">ClickToFly Pricing Configuration</h2>

      <div className="ctf-config-container">
        {/* Master Switch */}
        <section className="ctf-config-section ctf-master-switch">
          <div className="ctf-switch-wrapper">
            <h3 className="ctf-switch-title">ClickToFly Master Switch</h3>
            <SwitchInput
              checked={state.config.enableClickToFly}
              onChange={handleEnableClickToFlyToggle}
              leftLabel="Disabled"
              rightLabel="Enabled"
            />
          </div>
        </section>

        {/* Member Discount Rates */}
        <section className="ctf-config-section">
          <h3 className="ctf-section-title">Member Discount Rates</h3>
          <p className="ctf-section-description">
            Configure discount rates for members and non-members
          </p>

          <div className="ctf-discount-rates">
            <div className="ctf-form-group">
              <label htmlFor="nonMemberDiscountRate">
                Non-Member Discount %
              </label>
              <div className="ctf-percentage-input">
                <input
                  type="number"
                  id="nonMemberDiscountRate"
                  step="0.01"
                  value={(state.config.nonMemberDiscountRate * 100).toFixed(2)}
                  onChange={handleNonMemberDiscountRateChange}
                />
                <span className="ctf-percentage-symbol">%</span>
              </div>
            </div>

            <div className="ctf-form-group">
              <label htmlFor="memberDiscountRate">Member Discount %</label>
              <div className="ctf-percentage-input">
                <input
                  type="number"
                  id="memberDiscountRate"
                  step="0.01"
                  value={(state.config.memberDiscountRate * 100).toFixed(2)}
                  onChange={handleMemberDiscountRateChange}
                />
                <span className="ctf-percentage-symbol">%</span>
              </div>
            </div>
          </div>

          <div className="ctf-section-actions">
            <button
              type="button"
              className="ctf-btn ctf-btn-primary"
              onClick={handleSaveMemberDiscount}
            >
              Save Discount Rates
            </button>
          </div>
        </section>

        {/* Section 1: YOM Adjustment Factors - TABBED */}
        <section className="ctf-config-section">
          <h3 className="ctf-section-title">Year of Manufacture Adjustments</h3>
          <p className="ctf-section-description">
            Configure pricing adjustments based on aircraft year of manufacture.
            Each aircraft type can have up to 4 YOM tiers.
          </p>

          {/* Tab Navigation */}
          <div className="ctf-tabs">
            {filteredYomFactors.map((factor, index) => (
              <button
                key={
                  factor.aircraftTypeId._id ||
                  factor.aircraftTypeId.$oid ||
                  factor.aircraftTypeId
                }
                type="button"
                className={`ctf-tab ${
                  activeYomTab === index ? "ctf-tab-active" : ""
                }`}
                onClick={() => setActiveYomTab(index)}
              >
                {factor.aircraftTypeName}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="ctf-tab-content">
            {filteredYomFactors.map((factor, index) => (
              <div
                key={
                  factor.aircraftTypeId._id ||
                  factor.aircraftTypeId.$oid ||
                  factor.aircraftTypeId
                }
                className={`ctf-tab-panel ${
                  activeYomTab === index ? "ctf-tab-panel-active" : ""
                }`}
              >
                {activeYomTab === index && (
                  <>
                    <YomTierRepeater
                      tiers={factor.yomTiers}
                      aircraftTypeId={
                        factor.aircraftTypeId._id ||
                        factor.aircraftTypeId.$oid ||
                        factor.aircraftTypeId
                      }
                      aircraftTypeName={factor.aircraftTypeName}
                      onAdd={handleYomTierAdd}
                      onRemove={handleYomTierRemove}
                      onChange={handleYomTierChange}
                      maxTiers={4}
                    />
                    <div className="ctf-tab-actions">
                      <button
                        type="button"
                        className="ctf-btn ctf-btn-primary"
                        onClick={() =>
                          handleSaveAircraftTypeYom(
                            factor.aircraftTypeId._id ||
                              factor.aircraftTypeId.$oid ||
                              factor.aircraftTypeId
                          )
                        }
                      >
                        Save {factor.aircraftTypeName} Config
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Section 1.5: WiFi Add-On Configuration */}
        <section className="ctf-config-section">
          <h3 className="ctf-section-title">WiFi Add-On Configuration</h3>
          <p className="ctf-section-description">
            Configure WiFi add-on costs per aircraft type and year of manufacture.
            Leave percentage empty to indicate WiFi is not available for that year.
          </p>

          {/* Tab Navigation */}
          <div className="ctf-tabs">
            {filteredYomFactors.map((factor, index) => (
              <button
                key={
                  factor.aircraftTypeId._id ||
                  factor.aircraftTypeId.$oid ||
                  factor.aircraftTypeId
                }
                type="button"
                className={`ctf-tab ${
                  activeWifiTab === index ? "ctf-tab-active" : ""
                }`}
                onClick={() => setActiveWifiTab(index)}
              >
                {factor.aircraftTypeName}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="ctf-tab-content">
            {filteredYomFactors.map((factor, index) => {
              const aircraftTypeId =
                factor.aircraftTypeId._id ||
                factor.aircraftTypeId.$oid ||
                factor.aircraftTypeId;
              const wifiFactor = (state.config.wifiAddOnFactors || []).find(
                (w) =>
                  (w.aircraftTypeId._id ||
                    w.aircraftTypeId.$oid ||
                    w.aircraftTypeId) === aircraftTypeId
              );

              return (
                <div
                  key={aircraftTypeId}
                  className={`ctf-tab-panel ${
                    activeWifiTab === index ? "ctf-tab-panel-active" : ""
                  }`}
                >
                  {activeWifiTab === index && (
                    <>
                      <WifiTierRepeater
                        tiers={wifiFactor?.wifiTiers || []}
                        aircraftTypeId={aircraftTypeId}
                        aircraftTypeName={factor.aircraftTypeName}
                        onAdd={handleWifiTierAdd}
                        onRemove={handleWifiTierRemove}
                        onChange={handleWifiTierChange}
                        maxTiers={4}
                      />
                      <div className="ctf-tab-actions">
                        <button
                          type="button"
                          className="ctf-btn ctf-btn-primary"
                          onClick={() =>
                            handleSaveAircraftTypeWifi(aircraftTypeId)
                          }
                        >
                          Save {factor.aircraftTypeName} WiFi Config
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 2: Time Adjustment Factors */}
        <section className="ctf-config-section">
          <h3 className="ctf-section-title">Time Flexibility Adjustments</h3>
          <p className="ctf-section-description">
            Configure pricing adjustments for flexible departure time windows.
          </p>

          {state.config.timeAdjustmentFactors.map((factor, index) => (
            <div key={index} className="ctf-time-row">
              <div className="ctf-form-group">
                <label htmlFor={`timeLabel-${index}`}>Label</label>
                <input
                  type="text"
                  id={`timeLabel-${index}`}
                  value={factor.label}
                  placeholder="e.g., Morning flex"
                  onChange={(e) =>
                    handleTimeFactorChange(index, "label", e.target.value)
                  }
                />
              </div>

              <div className="ctf-form-group">
                <label htmlFor={`startTime-${index}`}>Start Hour</label>
                <input
                  type="number"
                  id={`startTime-${index}`}
                  min="0"
                  max="23"
                  value={factor.startTime}
                  onChange={(e) =>
                    handleTimeFactorChange(index, "startTime", e.target.value)
                  }
                />
                <small className="ctf-helper-text">
                  {formatHour(factor.startTime)}
                </small>
              </div>

              <div className="ctf-form-group">
                <label htmlFor={`endTime-${index}`}>End Hour</label>
                <input
                  type="number"
                  id={`endTime-${index}`}
                  min="0"
                  max="23"
                  value={factor.endTime}
                  onChange={(e) =>
                    handleTimeFactorChange(index, "endTime", e.target.value)
                  }
                />
                <small className="ctf-helper-text">
                  {formatHour(factor.endTime)}
                </small>
              </div>

              <div className="ctf-form-group">
                <label htmlFor={`timeAdjustment-${index}`}>Adjustment %</label>
                <div className="ctf-percentage-input">
                  <input
                    type="number"
                    id={`timeAdjustment-${index}`}
                    step="0.01"
                    value={toPercentageDisplay(factor.adjustment)}
                    placeholder="e.g., -2"
                    onChange={(e) =>
                      handleTimeFactorChange(
                        index,
                        "adjustment",
                        e.target.value
                      )
                    }
                  />
                  <span className="ctf-percentage-symbol">%</span>
                </div>
              </div>

              <button
                type="button"
                className="ctf-btn ctf-btn-danger ctf-btn-remove"
                onClick={() => handleTimeRangeRemove(index)}
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            className="ctf-btn ctf-btn-secondary"
            onClick={handleTimeRangeAdd}
          >
            + Add Time Range
          </button>

          <div className="ctf-section-actions">
            <button
              type="button"
              className="ctf-btn ctf-btn-primary"
              onClick={handleSaveTimeAdjustments}
            >
              Save Time Adjustments
            </button>
          </div>
        </section>

        {/* Exact Time Selection Configuration */}
        <section className="ctf-config-section">
          <h3 className="ctf-section-title">Exact Time Selection</h3>
          <p className="ctf-section-description">
            Configure the time picker parameters for exact departure time
            selection (0% discount option).
          </p>

          {state.config.exactTimeSelection && (
            <div className="ctf-time-row">
              <div className="ctf-form-group">
                <label htmlFor="exactStartTime">Start Hour</label>
                <input
                  type="number"
                  id="exactStartTime"
                  min="0"
                  max="23"
                  value={state.config.exactTimeSelection.startTime}
                  onChange={(e) =>
                    handleExactTimeSelectionChange("startTime", e.target.value)
                  }
                />
                <small className="ctf-helper-text">
                  {formatHour(state.config.exactTimeSelection.startTime)}
                </small>
              </div>

              <div className="ctf-form-group">
                <label htmlFor="exactEndTime">End Hour</label>
                <input
                  type="number"
                  id="exactEndTime"
                  min="0"
                  max="23"
                  value={state.config.exactTimeSelection.endTime}
                  onChange={(e) =>
                    handleExactTimeSelectionChange("endTime", e.target.value)
                  }
                />
                <small className="ctf-helper-text">
                  {formatHour(state.config.exactTimeSelection.endTime)}
                </small>
              </div>

              <div className="ctf-form-group">
                <label htmlFor="exactStepInterval">Step Interval (hours)</label>
                <input
                  type="number"
                  id="exactStepInterval"
                  min="1"
                  max="4"
                  value={state.config.exactTimeSelection.stepInterval}
                  onChange={(e) =>
                    handleExactTimeSelectionChange(
                      "stepInterval",
                      e.target.value
                    )
                  }
                />
              </div>

              <div className="ctf-form-group">
                <label htmlFor="exactAdjustment">Adjustment %</label>
                <div className="ctf-percentage-input">
                  <input
                    type="number"
                    id="exactAdjustment"
                    step="0.01"
                    value={toPercentageDisplay(
                      state.config.exactTimeSelection.adjustment
                    )}
                    onChange={(e) =>
                      handleExactTimeSelectionChange(
                        "adjustment",
                        e.target.value
                      )
                    }
                  />
                  <span className="ctf-percentage-symbol">%</span>
                </div>
              </div>
            </div>
          )}

          <div className="ctf-section-actions">
            <button
              type="button"
              className="ctf-btn ctf-btn-primary"
              onClick={handleSaveExactTimeSelection}
            >
              Save Exact Time Selection
            </button>
          </div>
        </section>

        {/* Section 3: Peak Day Notice Requirements */}
        <section className="ctf-config-section">
          <h3 className="ctf-section-title">Peak Day Notice Requirements</h3>
          <p className="ctf-section-description">
            Configure minimum days notice required for bookings during different
            peak day categories.
            <br />
            <small>
              OW/PPT2 = One Way / PPT2 trips | OTG/DRT = On-the-Ground / DRT
              trips
            </small>
          </p>

          <div className="ctf-peak-table">
            <table>
              <thead>
                <tr>
                  <th>Peak Category</th>
                  <th>OW/PPT2 Days</th>
                  <th>OTG/DRT Days</th>
                </tr>
              </thead>
              <tbody>
                {state.config.peakDayNoticeRequirements.map((req, index) => (
                  <tr key={index}>
                    <td className="ctf-peak-category-cell">
                      {getPeakCategoryLabel(req.peakDayCategoryId)}
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        value={req.owPpt2Days}
                        onChange={(e) =>
                          handlePeakNoticeChange(
                            index,
                            "owPpt2Days",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        value={req.otgDrtDays}
                        onChange={(e) =>
                          handlePeakNoticeChange(
                            index,
                            "otgDrtDays",
                            e.target.value
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ctf-section-actions">
            <button
              type="button"
              className="ctf-btn ctf-btn-primary"
              onClick={handleSavePeakNotice}
            >
              Save Peak Notice Requirements
            </button>
          </div>
        </section>

        {/* Section 4: Price Range Adjustments */}
        <section className="ctf-config-section">
          <h3 className="ctf-section-title">
            Price Range ClickToFly Adjustments
          </h3>
          <p className="ctf-section-description">
            Configure pricing adjustments based on estimate price ranges.
            <br />
            <small>
              Adjustments are applied as multipliers (e.g., 112.5% = 1.125x the
              base price)
            </small>
          </p>

          <div className="ctf-distance-table">
            <table>
              <thead>
                <tr>
                  <th>Min Price ($)</th>
                  <th>Max Price ($)</th>
                  <th>OW/PPT2/DRT Adj %</th>
                  <th>OTG Adj %</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {state.config.priceRangeAdjustments &&
                  state.config.priceRangeAdjustments.map((range, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="number"
                          min="0"
                          value={range.minPrice}
                          onChange={(e) =>
                            handlePriceRangeChange(
                              index,
                              "minPrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          value={range.maxPrice}
                          placeholder="999999 for ∞"
                          onChange={(e) =>
                            handlePriceRangeChange(
                              index,
                              "maxPrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <div className="ctf-percentage-input">
                          <input
                            type="number"
                            step="0.01"
                            value={(range.owPpt2DrtAdjustment * 100).toFixed(2)}
                            onChange={(e) =>
                              handlePriceRangeChange(
                                index,
                                "owPpt2DrtAdjustment",
                                e.target.value
                              )
                            }
                          />
                          <span className="ctf-percentage-symbol">%</span>
                        </div>
                      </td>
                      <td>
                        <div className="ctf-percentage-input">
                          <input
                            type="number"
                            step="0.01"
                            value={(range.otgAdjustment * 100).toFixed(2)}
                            onChange={(e) =>
                              handlePriceRangeChange(
                                index,
                                "otgAdjustment",
                                e.target.value
                              )
                            }
                          />
                          <span className="ctf-percentage-symbol">%</span>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ctf-btn ctf-btn-danger ctf-btn-small"
                          onClick={() => handlePriceRangeRemove(index)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            className="ctf-btn ctf-btn-secondary ctf-add-distance-btn"
            onClick={handlePriceRangeAdd}
          >
            + Add Price Range
          </button>

          <div className="ctf-section-actions">
            <button
              type="button"
              className="ctf-btn ctf-btn-primary"
              onClick={handleSavePriceRanges}
            >
              Save Price Adjustments
            </button>
          </div>
        </section>

        {/* Section 5: Departure TDR Flex Discounts */}
        <section className="ctf-config-section">
          <h3 className="ctf-section-title">Departure TDR Flex Discounts</h3>
          <p className="ctf-section-description">
            Configure discounts based on departure date flexibility (TDR = Trip
            Date Range).
            <br />
            <small>
              Discounts applied when customers select flexible departure dates
            </small>
          </p>

          <div className="ctf-tdr-table">
            <table>
              <thead>
                <tr>
                  <th>TDR Days</th>
                  <th>Discount %</th>
                </tr>
              </thead>
              <tbody>
                {state.config.departureTdrFlexDiscounts &&
                  state.config.departureTdrFlexDiscounts.map((tdr, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="number"
                          min="1"
                          value={tdr.tdrDays}
                          onChange={(e) =>
                            handleTdrFlexDiscountChange(
                              index,
                              "tdrDays",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <div className="ctf-percentage-input">
                          <input
                            type="number"
                            step="0.01"
                            value={(tdr.discount * 100).toFixed(2)}
                            onChange={(e) =>
                              handleTdrFlexDiscountChange(
                                index,
                                "discount",
                                e.target.value
                              )
                            }
                          />
                          <span className="ctf-percentage-symbol">%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="ctf-section-actions">
            <button
              type="button"
              className="ctf-btn ctf-btn-primary"
              onClick={handleSaveTdrFlexDiscounts}
            >
              Save TDR Flex Discounts
            </button>
          </div>
        </section>

        {/* Form Actions */}
        <div className="ctf-form-actions">
          {state.isDirty && (
            <span className="ctf-unsaved">* Unsaved changes</span>
          )}
          <button
            type="button"
            className="ctf-btn ctf-btn-secondary"
            onClick={handleReset}
            disabled={!state.isDirty || state.saving}
          >
            Reset All Changes
          </button>
          <button
            type="button"
            className="ctf-btn ctf-btn-primary"
            onClick={handleSubmit}
            disabled={!state.isDirty || state.saving}
          >
            {state.saving ? "Saving..." : "Save Full Configuration"}
          </button>
        </div>
      </div>
    </Fragment>
  );
};

export default ClickToFlyPricingConfigContent;
